import {
  type QueryFunctionContext,
  keepPreviousData,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompaniesByIdsAdmin } from './fetchCompaniesByIdsAdmin';
import { type CompanyByIdsAdmin } from './types';
import { zodCompaniesByIdsAdminPayloadSchema } from './validations';

/**
 * Generates query options for resolving multiple companies by ID as an admin.
 *
 * @param companyIds - Company IDs to resolve.
 * @param options - Additional options for the query.
 * @returns Query options for the company-by-IDs endpoint.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company
 */
export function getCompaniesByIdsAdminQueryOptions<
  TData = CompanyByIdsAdmin[],
>({
  companyIds,
  options,
}: {
  companyIds: readonly UUID[];
  options?: Omit<
    UseQueryOptions<CompanyByIdsAdmin[], Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) {
  const normalizedCompanyIds =
    zodCompaniesByIdsAdminPayloadSchema.safeParse({ companyIds }).data
      ?.companyIds ?? [];

  return queryOptions({
    placeholderData: keepPreviousData,
    queryFn: normalizedCompanyIds.length
      ? ({ signal }: QueryFunctionContext) =>
          fetchCompaniesByIdsAdmin(
            { companyIds: normalizedCompanyIds },
            { signal },
          )
      : skipToken,
    queryKey: queryKeysCompany.getByIdsAdmin(normalizedCompanyIds),
    staleTime: ms('60s'),
    ...options,
  });
}

/**
 * Resolves multiple companies by ID as an admin.
 *
 * @param companyIds - Company IDs to resolve.
 * @param options - Additional options for the query.
 * @returns The matching company summaries.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company
 */
export function useQueryCompaniesByIdsAdmin<TData = CompanyByIdsAdmin[]>(
  companyIds: readonly UUID[],
  options?: Omit<
    UseQueryOptions<CompanyByIdsAdmin[], Error, TData>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery(
    getCompaniesByIdsAdminQueryOptions<TData>({ companyIds, options }),
  );
}
